<template>
  <div class="flex flex-col gap-1 w-full">
    <div class="self-stretch justify-start items-center gap-2 inline-flex">
      <div class="grow shrink basis-0 text-white text-sm font-medium">
        {{ label }}
      </div>
      <div v-if="required" class="text-white/80 text-xs font-semibold">
        {{ $t("ui.form.required") }}
      </div>
    </div>
    <div
      class="self-stretch h-10 px-4 bg-surface rounded-lg border-2 border-fill-brand justify-start items-center gap-2 inline-flex"
      :class="[{ 'border-basics-accent-red': error }, containerClass]"
    >
      <slot name="prefix" />
      <input
        :value="safeValue"
        class="grow shrink basis-0 text-white text-sm font-medium bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        :placeholder="placeholder"
        :readonly="readonly"
        :type="type"
        v-bind="$attrs"
        @input="safeOnInput"
        @keydown="handleKeydown"
        @wheel="handleWheel"
      />
    </div>
    <span v-if="error" class="text-xs text-basics-accent-red">
      {{ error }}
    </span>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="T extends string | number | boolean | undefined | null"
>
import { computed } from "vue";
import { toSafeNumber } from "@/utils/data-types/numbers";
import { toSafeString } from "@/utils/data-types/strings";

const model = defineModel<T>({ required: true });

const props = defineProps<{
  label: string;
  placeholder?: string;
  required?: boolean;
  readonly?: boolean;
  error?: string;
  type?: "text" | "number" | "email" | "password";
  containerClass?: string;
  disableNumberControls?: boolean;
}>();

const handleKeydown = (event: KeyboardEvent) => {
  if (props.disableNumberControls && props.type === "number") {
    // Prevent arrow up/down keys from changing the value
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
    }
  }
};

const handleWheel = (event: WheelEvent) => {
  if (props.disableNumberControls && props.type === "number") {
    // Prevent mouse wheel from changing the value when input is focused
    const target = event.target as HTMLInputElement;
    if (document.activeElement === target) {
      event.preventDefault();
    }
  }
};

const safeValue = computed(() => {
  if (props.type === "number") {
    return toSafeNumber(model.value);
  }

  return toSafeString(model.value);
});

const safeOnInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value: string | number | boolean | undefined | null = target.value;

  if (props.type === "number") {
    value = toSafeNumber(value);
  } else {
    value = toSafeString(value);
  }

  model.value = value as T;
};
</script>
